import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { THEME, gradients, shadows } from '../constants/theme';

const About = () => {
  const [activeSection, setActiveSection] = useState('our-story');

  const sections = [
    { id: 'our-story', title: 'Our Story', icon: '📖' },
    { id: 'our-mission', title: 'Our Mission', icon: '🚀' },
    { id: 'our-team', title: 'Our Team', icon: '👥' },
    { id: 'our-technology', title: 'Our Technology', icon: '💻' },
    { id: 'our-values', title: 'Our Values', icon: '🌟' },
    { id: 'contact-us', title: 'Contact Us', icon: '📧' },
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setActiveSection(sectionId);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: THEME.background }}>
      {/* Fixed Sidebar */}
      <div className="fixed top-0 left-0 h-full w-64 overflow-y-auto"
        style={{ backgroundColor: THEME.cardbg, boxShadow: shadows.lg }}>
        <div className="p-6" style={{ background: gradients.primary }}>
          <Link to="/" className="flex items-center space-x-2">
            <img src="/gradient_logo.png" alt="Aided" className="h-8 w-8" />
            <span className="text-xl font-bold text-white">Aided</span>
          </Link>
        </div>
        <nav className="p-4">
          <h2 className="text-lg font-semibold mb-4" style={{ color: THEME.text }}>About Aided</h2>
          <ul className="space-y-2">
            {sections.map((section) => (
              <li key={section.id}>
                <button
                  onClick={() => scrollToSection(section.id)}
                  className={`w-full text-left px-3 py-2 rounded-md flex items-center transition-colors ${
                    activeSection === section.id
                      ? 'bg-primary-light bg-opacity-20 text-primary-dark'
                      : 'hover:bg-gray-100'
                  }`}
                  style={{ color: activeSection === section.id ? THEME.primary : THEME.textSecondary }}
                >
                  <span className="mr-2">{section.icon}</span>
                  <span>{section.title}</span>
                  {activeSection === section.id && (
                    <ChevronRightIcon className="ml-auto h-4 w-4" style={{ color: THEME.primary }} />
                  )}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      </div>

      {/* Main Content */}
      <div className="ml-64 p-8">
        <div className="max-w-4xl mx-auto">
          <header className="mb-12 border-b pb-8" style={{ borderColor: THEME.border }}>
            <h1 className="text-4xl font-bold mb-4" style={{ color: THEME.text }}>About Aided</h1>
            <p className="text-lg" style={{ color: THEME.textSecondary }}>
              Learn about our journey, mission, and the team behind Aided.
            </p>
          </header>

          <section id="our-story" className="mb-16">
            <h2 className="text-2xl font-semibold mb-6" style={{ color: THEME.text }}>Our Story</h2>
            <div className="prose max-w-none" style={{ color: THEME.textSecondary }}>
              <p className="mb-4">
                Aided was born out of a simple observation: small business owners and sole traders spend too much time on administrative tasks and not enough time doing what they love - running their business and serving their customers.
              </p>
              <p className="mb-4">
                Founded in 2023, our team of entrepreneurs and developers set out to create a solution that would leverage the power of artificial intelligence to automate the most time-consuming aspects of business administration.
              </p>
              <p className="mb-4">
                What started as a simple expense tracking tool has evolved into a comprehensive business administration platform that helps thousands of small businesses streamline their operations, improve cash flow, and focus on growth.
              </p>
            </div>
          </section>

          <section id="our-mission" className="mb-16">
            <h2 className="text-2xl font-semibold mb-6" style={{ color: THEME.text }}>Our Mission</h2>
            <div className="prose max-w-none" style={{ color: THEME.textSecondary }}>
              <p className="mb-4">
                At Aided, our mission is to empower small businesses and sole traders with intelligent tools that simplify administration, enhance productivity, and drive growth.
              </p>
              <p className="mb-4">
                We believe that by automating routine tasks and providing actionable insights, we can help business owners focus on what truly matters - delivering value to their customers and growing their business.
              </p>
              <p className="mb-4">
                Our goal is to become the most trusted AI-powered business administration platform for small businesses worldwide, saving our users thousands of hours and helping them achieve financial success.
              </p>
            </div>
          </section>

          <section id="our-team" className="mb-16">
            <h2 className="text-2xl font-semibold mb-6" style={{ color: THEME.text }}>Our Team</h2>
            <div className="prose max-w-none" style={{ color: THEME.textSecondary }}>
              <p className="mb-4">
                Aided is built by a passionate team of entrepreneurs, developers, designers, and AI specialists who are committed to creating exceptional tools for small businesses.
              </p>
              <p className="mb-4">
                Our diverse team brings together expertise from various fields including finance, technology, and small business management. This diversity of perspective allows us to create solutions that truly address the real-world challenges faced by our users.
              </p>
              <p className="mb-4">
                We operate with a remote-first approach, with team members across Australia and around the world, allowing us to bring together the best talent regardless of location.
              </p>
            </div>
          </section>

          <section id="our-technology" className="mb-16">
            <h2 className="text-2xl font-semibold mb-6" style={{ color: THEME.text }}>Our Technology</h2>
            <div className="prose max-w-none" style={{ color: THEME.textSecondary }}>
              <p className="mb-4">
                At the heart of Aided is our proprietary AI technology that powers everything from expense recognition to invoice generation and financial insights.
              </p>
              <p className="mb-4">
                We've developed advanced machine learning models that can accurately extract information from receipts, categorize expenses, and learn from user behavior to become more efficient over time.
              </p>
              <p className="mb-4">
                Our platform is built on a secure, scalable cloud infrastructure that ensures your data is always safe, accessible, and protected by industry-leading security measures.
              </p>
              <p className="mb-4">
                We're constantly innovating and improving our technology, with regular updates and new features based on user feedback and emerging technologies.
              </p>
            </div>
          </section>

          <section id="our-values" className="mb-16">
            <h2 className="text-2xl font-semibold mb-6" style={{ color: THEME.text }}>Our Values</h2>
            <div className="prose max-w-none" style={{ color: THEME.textSecondary }}>
              <p className="mb-4">
                <strong>User-Centric:</strong> Everything we do starts with our users. We listen to their needs, incorporate their feedback, and build solutions that make their lives easier.
              </p>
              <p className="mb-4">
                <strong>Innovation:</strong> We embrace new technologies and ideas, constantly pushing the boundaries of what's possible to deliver better solutions for our users.
              </p>
              <p className="mb-4">
                <strong>Integrity:</strong> We operate with honesty, transparency, and respect for our users' data and privacy. Trust is the foundation of our relationship with our users.
              </p>
              <p className="mb-4">
                <strong>Simplicity:</strong> We believe in making complex tasks simple. Our platform is designed to be intuitive, accessible, and easy to use for everyone.
              </p>
              <p className="mb-4">
                <strong>Impact:</strong> We measure our success by the positive impact we have on our users' businesses. Every feature we build aims to save time, reduce stress, or improve financial outcomes.
              </p>
            </div>
          </section>

          <section id="contact-us" className="mb-16">
            <h2 className="text-2xl font-semibold mb-6" style={{ color: THEME.text }}>Contact Us</h2>
            <div className="prose max-w-none" style={{ color: THEME.textSecondary }}>
              <p className="mb-4">
                We'd love to hear from you! Whether you have questions about our platform, feedback on your experience, or just want to say hello, our team is here to help.
              </p>
              <p className="mb-4">
                <strong>Email:</strong> <a href="mailto:support@aided.com.au" className="text-primary-dark hover:underline">support@aided.com.au</a>
              </p>
              <p className="mb-4">
                <strong>Support:</strong> Visit our <Link to="/support" className="text-primary-dark hover:underline">Support Center</Link> for help with specific questions or issues.
              </p>
              <p className="mb-4">
                <strong>Documentation:</strong> Check out our <Link to="/docs" className="text-primary-dark hover:underline">Documentation</Link> for detailed guides on using Aided.
              </p>
              <p className="mb-4">
                Follow us on social media for the latest updates, tips, and news about Aided.
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default About; 